import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { AUTH_URL } from 'src/configs';
import request from 'src/request';

interface PresaleState {
  data: {
    presaleUsers: object[];
    totalCount: number;
    totalPages: number;
    field: string;
    search: string;
  };
  loading: boolean;
  error: string | null;
}

const initialState: PresaleState = {
  data: {
    presaleUsers: [],
    totalCount: 0,
    totalPages: 0,
    field: 'Referral Account',
    search: '',
  },
  loading: false,
  error: null,
};

const toPresaleUserSearchParam = (field?: string) => {
  switch (field) {
    case 'Referral Account':
      return 'referral_account';
    case 'Receiving Wallet':
      return 'receiving_wallet';
    default:
      return '';
  }
};

export const fetchPresaleUsers = createAsyncThunk(
  'components/fetchPresaleUsers',
  async ({ page, field, search }: { page: number; field?: string; search?: string }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '10',
    });

    if (search) {
      const param = toPresaleUserSearchParam(field);
      if (param) params.set(param, search);
    }

    const response = await request.get(`${AUTH_URL}/presale/admin/presale-user?${params.toString()}`);
    return {
      data: response.data.presale_user,
      totalCount: response.data.meta.total_count,
      totalPages: response.data.meta.page_count,
      page,
      field,
      search,
    };
  }
);

const kycData = createSlice({
  name: 'presale-users',
  initialState,
  reducers: {
    updateField: (state, action) => {
      const { field } = action.payload;
      state.data.field = field;
    },
    updateSearch: (state, action) => {
      const { search } = action.payload;
      state.data.search = search;
    },
    resetSearch: (state) => {
      state.data.search = '';
      state.data.field = 'Referral Account';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPresaleUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPresaleUsers.fulfilled, (state, action) => {
        state.data = {
          presaleUsers: action.payload.data,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
          field: action.payload.field ?? 'Referral Account',
          search: action.payload.search ?? '',
        };
        state.loading = false;
      })
      .addCase(fetchPresaleUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch pre-sale users';
      });
  },
});

export const { updateField, updateSearch, resetSearch } = kycData.actions;

export default kycData.reducer;
